import { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ApiError, apiClient } from '../../lib/api';
import {
  CandidateDto,
  ConversionSnapshotDto,
  FieldProblemDto,
  ModeDto,
  PrepareResultDto,
  ScreenDto,
  SubmitResultDto,
  SubmitScreenDto,
} from '../../lib/convertTypes';
import { ScreenRenderer } from './ScreenRenderer';

type Stage = 'mode' | 'upload' | 'pick' | 'candidates' | 'results';

/**
 * The conversion flow, mirroring the wizard's ten steps: pick the test-type mode (never
 * inferred), upload the input file(s), preflight, walk each detected candidate's review
 * screens, then download the EPA-ready JSON and its audit sidecar.
 */
export function ConvertFlow() {
  const [stage, setStage] = useState<Stage>('mode');
  const [mode, setMode] = useState<ModeDto | null>(null);
  const [conversion, setConversion] = useState<ConversionSnapshotDto | null>(null);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [prepare, setPrepare] = useState<PrepareResultDto | null>(null);
  const [candidates, setCandidates] = useState<CandidateDto[]>([]);
  // The pick stage: every detected test is ticked by default; a workbook that carries more RATAs than
  // the job reports (JEA 26-3831: CO2, NOX and SO2 all have run data, two were wanted) unticks the rest.
  const [detected, setDetected] = useState<CandidateDto[]>([]);
  const [picked, setPicked] = useState<Record<string, boolean>>({});
  const [candidateIndex, setCandidateIndex] = useState(0);
  const [screen, setScreen] = useState<ScreenDto | null>(null);
  const [problems, setProblems] = useState<FieldProblemDto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const { data: modes } = useQuery({
    queryKey: ['modes'],
    queryFn: () => apiClient.get<ModeDto[]>('/api/modes'),
  });

  // The mode used last session is highlighted as the default pick (localstate parity).
  const { data: preferences } = useQuery({
    queryKey: ['preferences'],
    queryFn: () => apiClient.get<{ lastModeId?: string }>('/api/me/preferences'),
  });

  const currentParameter = candidates[candidateIndex]?.parameter;

  const loadScreen = useCallback(async (conversionId: string, parameter: string) => {
    const s = await apiClient.get<ScreenDto>(
      `/api/conversions/${conversionId}/candidates/${encodeURIComponent(parameter)}/screen`);
    setScreen(s);
    setProblems([]);
  }, []);

  useEffect(() => {
    if (stage === 'candidates' && conversion && currentParameter) {
      void loadScreen(conversion.id, currentParameter);
    }
  }, [stage, conversion, currentParameter, loadScreen]);

  async function begin(picked: ModeDto) {
    setMode(picked);
    setError(null);
    const snapshot = await apiClient.post<ConversionSnapshotDto>('/api/conversions', { modeId: picked.id });
    setConversion(snapshot);
    setPendingFiles([]);
    setStage('upload');
  }

  async function uploadAndPrepare() {
    if (!conversion || pendingFiles.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      const form = new FormData();
      for (const file of pendingFiles) form.append('files', file, file.name);
      await apiClient.postForm<ConversionSnapshotDto>(`/api/conversions/${conversion.id}/files`, form);

      const result = await apiClient.post<PrepareResultDto>(`/api/conversions/${conversion.id}/prepare`, {});
      setPrepare(result);
      setDetected(result.candidates);
      setPicked(Object.fromEntries(result.candidates.map((c) => [c.parameter, true])));
      setCandidateIndex(0);
      if (result.candidates.length > 1) {
        // More than one test under test: let the operator choose which to walk (one test needs no pick).
        setCandidates([]);
        setStage('pick');
      } else {
        setCandidates(result.candidates);
        setStage('candidates');
      }
    } catch (e) {
      setPrepare(extractPrepare(e) ?? null);
      setError(describeError(e));
    } finally {
      setBusy(false);
    }
  }

  async function submit(dto: SubmitScreenDto) {
    if (!conversion || !currentParameter) return;
    setBusy(true);
    setError(null);
    try {
      const result = await apiClient.post<SubmitResultDto>(
        `/api/conversions/${conversion.id}/candidates/${encodeURIComponent(currentParameter)}/answers`, dto);
      applySubmit(result);
    } catch (e) {
      const body = (e as ApiError).body as SubmitResultDto | undefined;
      if (body?.problems) applySubmit(body);
      else setError(describeError(e));
    } finally {
      setBusy(false);
    }
  }

  function applySubmit(result: SubmitResultDto) {
    if (result.problems.length > 0) {
      setProblems(result.problems);
      return;
    }
    setProblems([]);
    if (result.next) setScreen(result.next);
  }

  async function goBack() {
    if (!conversion || !currentParameter) return;
    const s = await apiClient.post<ScreenDto>(
      `/api/conversions/${conversion.id}/candidates/${encodeURIComponent(currentParameter)}/back`, {});
    setScreen(s);
    setProblems([]);
  }

  async function nextCandidate() {
    if (candidateIndex + 1 < candidates.length) {
      setCandidateIndex(candidateIndex + 1);
      setScreen(null);
    } else if (conversion) {
      const snapshot = await apiClient.get<ConversionSnapshotDto>(`/api/conversions/${conversion.id}`);
      setConversion(snapshot);
      setStage('results');
    }
  }

  function startWalk() {
    const chosen = detected.filter((c) => picked[c.parameter]);
    if (chosen.length === 0) return;
    setCandidates(chosen);
    setCandidateIndex(0);
    setScreen(null);
    setStage('candidates');
  }

  function restart() {
    setStage('mode');
    setMode(null);
    setConversion(null);
    setPrepare(null);
    setCandidates([]);
    setDetected([]);
    setPicked({});
    setScreen(null);
    setError(null);
  }

  return (
    <div className="max-w-3xl space-y-6">
      {error && (
        <p className="text-sm bg-red-50 border border-red-300 text-red-800 rounded-md px-3 py-2">{error}</p>
      )}

      {stage === 'mode' && (
        <div className="space-y-4">
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-500">Step 2 of 10 — What are you converting?</div>
            <h2 className="text-xl font-semibold text-atg-navy">What type of test are you converting?</h2>
          </div>
          <div className="grid gap-3">
            {(modes ?? []).map((m) => (
              <button
                key={m.id}
                onClick={() => void begin(m)}
                className={`text-left border bg-white rounded-md px-4 py-3 hover:border-atg-navy ${
                  preferences?.lastModeId === m.id ? 'border-atg-navy' : 'border-slate-200'}`}
              >
                <div className="font-medium">
                  {m.label}
                  {preferences?.lastModeId === m.id && (
                    <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">last used</span>
                  )}
                </div>
                <div className="text-xs text-slate-500">
                  {m.maxInputFiles > 1 ? `1–${m.maxInputFiles} files` : 'one file'} · {m.extensions.join(' or ')}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {stage === 'upload' && mode && conversion && (
        <div className="space-y-4">
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-500">Step 3 of 10 — Pick the {mode.inputNoun}</div>
            <h2 className="text-xl font-semibold text-atg-navy">{mode.label}</h2>
          </div>
          <p className="text-sm text-slate-600">
            {mode.maxInputFiles > 1
              ? `Pick one field ${mode.inputNoun} per load level (up to ${mode.maxInputFiles}). The level itself is read from the ${mode.inputNoun}, so order does not matter.`
              : `Pick the ${mode.inputNoun} (${mode.extensions.join(' or ')}).`}
          </p>
          <input
            type="file"
            multiple={mode.maxInputFiles > 1}
            accept={mode.extensions.join(',')}
            onChange={(e) => setPendingFiles(Array.from(e.target.files ?? []).slice(0, mode.maxInputFiles))}
            className="block text-sm"
          />
          {pendingFiles.length > 0 && (
            <ul className="text-sm text-slate-600 list-disc pl-5">
              {pendingFiles.map((f) => <li key={f.name}>{f.name}</li>)}
            </ul>
          )}

          {prepare && prepare.status !== 'prepared' && (
            <div className="text-sm space-y-1 bg-red-50 border border-red-300 rounded-md px-3 py-2">
              {prepare.failureReason && <p className="text-red-800">{prepare.failureReason}</p>}
              {prepare.preflight?.missing.map((m) => (
                <p key={m} className="text-red-800">Missing from the {mode.inputNoun}: {m}</p>
              ))}
              {prepare.extractionIssues.filter((i) => i.severity !== 'Info').map((i, idx) => (
                <p key={idx} className="text-red-800">{i.message}</p>
              ))}
            </div>
          )}

          <div className="flex gap-2">
            <button
              className="px-4 py-2 rounded bg-atg-navy text-white disabled:opacity-50"
              disabled={busy || pendingFiles.length === 0}
              onClick={() => void uploadAndPrepare()}
            >
              {busy ? 'Checking…' : `Check the ${mode.inputNoun}`}
            </button>
            <button className="px-4 py-2 rounded border border-slate-300" onClick={restart}>Start over</button>
          </div>
        </div>
      )}

      {stage === 'pick' && prepare?.preflight && mode && (
        <div className="space-y-4">
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-500">Step 4 of 10 — Checking the {mode.inputNoun}</div>
            <h2 className="text-xl font-semibold text-atg-navy">Which tests should be converted?</h2>
          </div>
          <p className="text-sm bg-green-50 border border-green-300 text-green-900 rounded-md px-3 py-2">
            Ready ✓ — found {detected.length} tests to convert:{' '}
            {detected.map((c) => `${c.parameter} (${prepare.preflight!.runsPerParameter[c.parameter] ?? '?'} runs)`).join(', ')}
          </p>
          <p className="text-sm text-slate-600">
            Every test is converted unless you untick it. An unticked test is skipped — nothing is written for it.
          </p>
          <ul className="space-y-2">
            {detected.map((c) => (
              <li key={c.parameter}>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={picked[c.parameter] ?? false}
                    onChange={(e) => setPicked({ ...picked, [c.parameter]: e.target.checked })}
                  />
                  <span>
                    Convert the {c.parameter} test ({prepare.preflight!.runsPerParameter[c.parameter] ?? '?'} runs)
                  </span>
                </label>
              </li>
            ))}
          </ul>
          <div className="flex gap-2">
            <button
              className="px-4 py-2 rounded bg-atg-navy text-white disabled:opacity-50"
              disabled={!detected.some((c) => picked[c.parameter])}
              onClick={startWalk}
            >
              Continue
            </button>
            <button className="px-4 py-2 rounded border border-slate-300" onClick={restart}>Start over</button>
          </div>
        </div>
      )}

      {stage === 'candidates' && prepare?.preflight && screen === null && (
        <p className="text-sm text-slate-500">Loading the {currentParameter} test…</p>
      )}

      {stage === 'candidates' && screen && conversion && (
        <div className="space-y-4">
          {prepare?.preflight && candidateIndex === 0 && screen.step === 5 && (
            <p className="text-sm bg-green-50 border border-green-300 text-green-900 rounded-md px-3 py-2">
              Ready ✓ — found {candidates.length} test{candidates.length === 1 ? '' : 's'} to convert:{' '}
              {candidates.map((c) => `${c.parameter} (${prepare.preflight!.runsPerParameter[c.parameter] ?? '?'} runs)`).join(', ')}
            </p>
          )}
          <ScreenRenderer screen={screen} busy={busy} problems={problems} onSubmit={(dto) => void submit(dto)} onBack={() => void goBack()} />
          {(screen.kind === 'done' || screen.kind === 'failed') && (
            <button className="px-4 py-2 rounded bg-atg-navy text-white" onClick={() => void nextCandidate()}>
              {candidateIndex + 1 < candidates.length
                ? `Next: the ${candidates[candidateIndex + 1].parameter} test`
                : 'Finish — show the files'}
            </button>
          )}
        </div>
      )}

      {stage === 'results' && conversion && (
        <div className="space-y-4">
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-500">Step 10 of 10 — Save & finish</div>
            <h2 className="text-xl font-semibold text-atg-navy">Your files</h2>
          </div>
          {conversion.outputs.length === 0 ? (
            <p className="text-sm text-slate-600">Nothing was written for this conversion.</p>
          ) : (
            <table className="text-sm w-full border-collapse bg-white border border-slate-200 rounded-md">
              <thead>
                <tr className="text-left text-slate-500">
                  <th className="p-2">File</th>
                  <th className="p-2">Test</th>
                  <th className="p-2">Downloads</th>
                </tr>
              </thead>
              <tbody>
                {conversion.outputs.map((o) => (
                  <tr key={o.index} className="border-t border-slate-100">
                    <td className="p-2 font-mono">{o.fileName}</td>
                    <td className="p-2">{o.parameter}</td>
                    <td className="p-2 space-x-3">
                      {o.hasOutput && (
                        <a className="text-atg-blue hover:underline"
                          href={`/api/conversions/${conversion.id}/outputs/${o.index}`}>JSON</a>
                      )}
                      <a className="text-atg-blue hover:underline"
                        href={`/api/conversions/${conversion.id}/outputs/${o.index}/audit`}>Audit</a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="flex gap-2">
            {conversion.outputs.length > 0 && (
              <a className="px-4 py-2 rounded bg-atg-navy text-white"
                href={`/api/conversions/${conversion.id}/outputs.zip`}>Download everything (.zip)</a>
            )}
            <button className="px-4 py-2 rounded border border-slate-300" onClick={restart}>
              Convert another
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function describeError(e: unknown): string {
  if (e instanceof ApiError) {
    const body = e.body as { title?: string; failureReason?: string } | undefined;
    return body?.title ?? body?.failureReason ?? `Request failed (${e.status}).`;
  }
  return 'Something went wrong — try again.';
}

function extractPrepare(e: unknown): PrepareResultDto | undefined {
  const body = (e as ApiError)?.body as PrepareResultDto | undefined;
  return body && typeof body.status === 'string' ? body : undefined;
}
